import { useEffect, useRef, useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { useServerDataTable } from '@/components/tables/useServerDataTable';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { formatNumber } from '@/utils/format';
import { onlineManagerApi } from '@/api/services/onlineManager';
import type { ApiLoginsRow, OnlineManagerFilterInput } from '@/api/types/onlineManager';
import { AddTransactionModal } from './AddTransactionModal';
import { AddCreditTransactionModal } from './AddCreditTransactionModal';
import { ChangePasswordModal } from './ChangePasswordModal';
import type { OnlineRowContext } from './RowContext';

type ModalKind = 'transaction' | 'credit' | 'password';

// Row actions as direct icon buttons (consistent with every other grid's Actions column,
// e.g. Identity Users), replacing the former kebab "More Actions" dropdown.
function RowActions({ onSelect }: { onSelect: (kind: ModalKind) => void }) {
  return (
    <div className="d-inline-flex gap-1 justify-content-center">
      <button type="button" className="btn btn-sm btn-outline-primary" title="Deposit / Withdrawal" onClick={() => onSelect('transaction')}>
        <i className="fa fa-exchange" />
      </button>
      <button type="button" className="btn btn-sm btn-outline-primary" title="Credit Facility" onClick={() => onSelect('credit')}>
        <i className="fa fa-credit-card" />
      </button>
      <button type="button" className="btn btn-sm btn-outline-primary" title="Change Password" onClick={() => onSelect('password')}>
        <i className="fa fa-key" />
      </button>
    </div>
  );
}

export function OnlineManagerPage() {
  const toast = useToast();
  const [modal, setModal] = useState<{ kind: ModalKind; ctx: OnlineRowContext } | null>(null);

  const table = useServerDataTable<ApiLoginsRow, OnlineManagerFilterInput>({
    queryKey: ['online-manager', 'rows'],
    fetcher: (req) => onlineManagerApi.getRows(req),
    pageSize: 5,
  });

  // Connector warning after each fetch settles (de-duped), errors swallowed.
  const wasFetching = useRef(false);
  const lastWarnRef = useRef<string>('');
  useEffect(() => {
    if (wasFetching.current && !table.isFetching) {
      onlineManagerApi.getConnectorWarning().then((r) => {
        const msg = r?.message ?? '';
        if (msg && msg !== lastWarnRef.current) {
          lastWarnRef.current = msg;
          toast.info(msg);
        }
      }).catch(() => {});
    }
    wasFetching.current = table.isFetching;
  }, [table.isFetching, toast]);

  const afterMutation = (verb: string) => {
    setModal(null);
    toast.success(verb);
    void table.refetch();
  };

  return (
    <div className="online-manager-page">
      <PageHeader title="Online Manager" breadcrumb={['Home', 'Online Manager']} />

      <DataGrid
        columns={[
          { key: 'login', title: 'Login', className: 'text-start', value: (r) => r.login },
          { key: 'group', title: 'Group', className: 'text-start', filterable: false, value: (r) => r.group },
          { key: 'currency', title: 'Currency', className: 'text-start', filterable: false, value: (r) => r.currency },
          { key: 'dataSource', title: 'Connector', className: 'text-start', filterable: false, value: (r) => r.dataSource },
          { key: 'balance', title: 'Balance', className: 'text-end', filterable: false, value: (r) => r.balance, cell: (r) => formatNumber(r.balance, 0) },
          { key: 'equity', title: 'Equity', className: 'text-end', filterable: false, value: (r) => r.equity, cell: (r) => formatNumber(r.equity, 0) },
          { key: 'credit', title: 'Credit', className: 'text-end', filterable: false, value: (r) => r.credit, cell: (r) => formatNumber(r.credit, 0) },
        ] as DataGridColumn<ApiLoginsRow>[]}
        data={table.rows}
        rowKey={(r) => `${r.login}-${r.dataSourceId}`}
        card
        includeExcel
        excelFileName="OnlineManager"
        columnSearch
        columnSearchValues={table.columnFilters}
        onColumnSearchChange={(key, value) => table.setColumnFilter(key, value)}
        emptyMessage={table.isLoading ? 'Loading…' : 'No data.'}
        toolbarStart={
          <button type="button" className="btn btn-sm btn-outline-primary" title="Reload Table" onClick={() => table.refetch()}>
            <i className="fa fa-refresh" />
          </button>
        }
        rowActions={(row) => (
          <RowActions onSelect={(kind) => setModal({ kind, ctx: { login: row.login, dataSourceId: row.dataSourceId, dataSourceName: row.dataSource } })} />
        )}
        server={{ pageIndex: table.pageIndex, pageSize: table.pageSize, pageCount: table.pageCount, totalCount: table.totalCount, onPageChange: table.setPageIndex, onPageSizeChange: table.setPageSize }}
      />

      {modal?.kind === 'transaction' && (
        <AddTransactionModal ctx={modal.ctx} onClose={() => setModal(null)} onSuccess={() => afterMutation('Transaction submitted successfully.')} />
      )}
      {modal?.kind === 'credit' && (
        <AddCreditTransactionModal ctx={modal.ctx} onClose={() => setModal(null)} onSuccess={() => afterMutation('Credit transaction submitted successfully.')} />
      )}
      {modal?.kind === 'password' && (
        <ChangePasswordModal ctx={modal.ctx} onClose={() => setModal(null)} onSuccess={() => afterMutation('Password changed successfully.')} />
      )}
    </div>
  );
}
